import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect, useCallback } from 'react'
import { useQuery as useConvexQuery, useAction } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useSongFormStorage } from '@/hooks/useSongFormStorage'
import { usePreviewSongStorage } from '@/hooks/usePreviewSongStorage'
import { SongCard } from '@/components/SongCard'
import { usePostHog } from '@/hooks/usePostHog'
import type { Id } from 'convex/_generated/dataModel'

export const Route = createFileRoute('/app/songs/')({
  component: SongsPage,
})

function SongsPage() {
  const currentUser = useCurrentUser()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Please sign in to view songs</div>
      </div>
    )
  }

  return <SongsPageContent userId={currentUser._id} />
}

function SongsPageContent({ userId }: { userId: Id<'users'> }) {
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'processing' | 'failed' | 'pending'>('all')

  // Get all user's generations - filter for song type only
  const allGenerations = useConvexQuery(api.tempolor.listByUser, {
    userId,
    limit: 100,
  })

  // Filter for songs only
  const songGenerations = allGenerations?.filter(gen => gen.type === 'song')

  // Filter generations based on status
  const filteredGenerations = songGenerations?.filter(gen =>
    statusFilter === 'all' ? true : gen.status === statusFilter
  )

  return (
    <div className="min-h-screen bg-black text-white py-8 px-4">
      <div className="max-w-full mx-auto">
        {/* Two Column Layout: 50% Creation Form | 50% Songs List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT: Song Creation Form - Sticky */}
          <div className="lg:sticky lg:top-8 lg:self-start">
            <SongCreationForm userId={userId} />
          </div>

          {/* RIGHT: Songs List */}
          <div>
            {/* Header */}
            <div className="mb-6 terminal-border p-6">
              <h2 className="text-3xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
                [YOUR SONGS]
              </h2>
              <p className="text-trail-tan font-mono mb-4">
                Generated songs with vocals and lyrics
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-black border-2 border-sky-blue p-3">
                  <div className="text-sky-blue text-2xl font-bold font-mono">{songGenerations?.length || 0}</div>
                  <div className="text-trail-tan text-xs font-mono">TOTAL</div>
                </div>
                <div className="bg-black border-2 border-terminal-green p-3">
                  <div className="text-terminal-green text-2xl font-bold font-mono">
                    {songGenerations?.filter(g => g.status === 'completed').length || 0}
                  </div>
                  <div className="text-trail-tan text-xs font-mono">DONE</div>
                </div>
                <div className="bg-black border-2 border-trail-tan p-3">
                  <div className="text-trail-tan text-2xl font-bold font-mono">
                    {songGenerations?.filter(g => g.status === 'processing').length || 0}
                  </div>
                  <div className="text-trail-tan text-xs font-mono">PROCESSING</div>
                </div>
                <div className="bg-black border-2 border-red-500 p-3">
                  <div className="text-red-500 text-2xl font-bold font-mono">
                    {songGenerations?.filter(g => g.status === 'failed').length || 0}
                  </div>
                  <div className="text-trail-tan text-xs font-mono">FAILED</div>
                </div>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="mb-6 flex gap-2 flex-wrap">
              {[
                { id: 'all' as const, label: 'ALL' },
                { id: 'completed' as const, label: 'DONE' },
                { id: 'processing' as const, label: 'PROCESSING' },
                { id: 'failed' as const, label: 'FAILED' },
              ].map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setStatusFilter(filter.id)}
                  className={`px-4 py-2 font-bold font-mono border-2 transition-all tracking-wider text-sm ${
                    statusFilter === filter.id
                      ? 'bg-sky-blue text-black border-sky-blue retro-glow'
                      : 'bg-black text-sky-blue border-sky-blue hover:bg-sky-blue hover:text-black'
                  }`}
                >
                  [{filter.label}]
                </button>
              ))}
            </div>

            {/* Songs List */}
            {!filteredGenerations ? (
              <div className="text-center text-trail-tan text-lg font-mono py-12">
                Loading songs...
              </div>
            ) : filteredGenerations.length === 0 ? (
              <div className="text-center terminal-border p-8">
                <div className="text-sky-blue text-xl font-bold font-mono mb-3">
                  NO {statusFilter.toUpperCase()} SONGS
                </div>
                <p className="text-trail-tan font-mono text-sm">
                  {statusFilter === 'all'
                    ? 'Create your first song using the form on the left!'
                    : `No ${statusFilter} songs found.`}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredGenerations.map((generation) => (
                  <SongCard 
                    key={generation._id} 
                    generation={{
                      ...generation,
                      status: generation.status as 'pending' | 'processing' | 'completed' | 'failed',
                    }} 
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function SongCreationForm({ userId }: { userId: Id<'users'> }) {
  const [prompt, setPrompt] = useState('')
  const [lyrics, setLyrics] = useState('')
  const [voiceId, setVoiceId] = useState('')
  const [title, setTitle] = useState('')
  const [tags, setTags] = useState('')
  const [makeInstrumental, setMakeInstrumental] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const generateSong = useAction(api.tempolor.generateSong)
  const generateInstrumental = useAction(api.tempolor.generateInstrumental)
  const { trackEvent } = usePostHog()

  // localStorage hook for form persistence (user-specific)
  const { loadFormState, saveFormState, clearFormState, startAutoSave } = useSongFormStorage(userId)
  
  // Generic localStorage hook for preview form data
  const { loadFormState: loadPreviewFormState, clearFormState: clearPreviewFormState } = usePreviewSongStorage()

  // Load saved form state on mount (both user-specific and preview)
  useEffect(() => {
    // First, try to load from preview localStorage (generic)
    const previewSaved = loadPreviewFormState()
    if (previewSaved) {
      // Use preview values as initial values if present and length > 0
      if (previewSaved.theme && previewSaved.theme.length > 0) setPrompt(previewSaved.theme)
      if (previewSaved.lyrics && previewSaved.lyrics.length > 0) setLyrics(previewSaved.lyrics)
      if (previewSaved.title && previewSaved.title.length > 0) setTitle(previewSaved.title)
      if (previewSaved.tags && previewSaved.tags.length > 0) setTags(previewSaved.tags)
      // Clear preview localStorage after loading
      clearPreviewFormState()
    }
    
    // Then load user-specific saved state (may override preview values)
    const saved = loadFormState()
    if (saved) {
      if (saved.prompt) setPrompt(saved.prompt)
      if (saved.lyrics) setLyrics(saved.lyrics)
      if (saved.voiceId) setVoiceId(saved.voiceId)
      if (saved.title) setTitle(saved.title)
      if (saved.tags) setTags(saved.tags)
      if (saved.makeInstrumental !== undefined) setMakeInstrumental(saved.makeInstrumental)
    }
  }, [loadFormState, loadPreviewFormState, clearPreviewFormState])

  // Setup auto-save interval
  useEffect(() => {
    startAutoSave(() => ({
      prompt,
      lyrics,
      voiceId,
      title,
      tags,
      makeInstrumental,
    }))
  }, [prompt, lyrics, voiceId, title, tags, makeInstrumental, startAutoSave])

  // Save to localStorage on blur
  const handleBlur = useCallback(() => {
    saveFormState({
      prompt,
      lyrics,
      voiceId,
      title,
      tags,
      makeInstrumental,
    })
  }, [prompt, lyrics, voiceId, title, tags, makeInstrumental, saveFormState])

  // Clear all form fields and localStorage
  const handleClearAll = useCallback(() => {
    setPrompt('')
    setLyrics('')
    setVoiceId('')
    setTitle('')
    setTags('')
    setMakeInstrumental(false)
    setValidationError(null)
    clearFormState()
  }, [clearFormState])

  // Clear generic localStorage (preview form data)
  const handleClearLocalStorage = useCallback(() => {
    clearPreviewFormState()
  }, [clearPreviewFormState])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setValidationError(null)

    // Route to appropriate action based on makeInstrumental toggle
    setIsGenerating(true)

    // Track song generation started
    trackEvent('song_generation_started', {
      songType: makeInstrumental ? 'instrumental' : 'song',
      model: makeInstrumental ? 'TemPolor i3.5' : 'TemPolor v3.5',
    })

    try {
      if (makeInstrumental) {
        // Generate instrumental music (no lyrics, no vocals)
        if (!prompt.trim()) {
          setValidationError('Prompt is required for instrumental generation')
          setIsGenerating(false)
          return
        }

        await generateInstrumental({
          userId,
          prompt: prompt.trim(),
          model: 'TemPolor i3.5', // Tempolor instrumental model
        })
      } else {
        // Generate song with vocals and lyrics
        if (!prompt.trim() || !lyrics.trim()) {
          setValidationError('Both prompt and lyrics are required for song generation')
          setIsGenerating(false)
          return
        }

        await generateSong({
          userId,
          prompt: prompt.trim(),
          model: 'TemPolor v3.5', // Tempolor song model (with vocals)
          lyrics: lyrics.trim(),
          voiceId: voiceId.trim() || undefined,
          title: title.trim() || undefined,
          tags: tags.trim() || undefined,
        })
      }

      // Track successful completion
      trackEvent('song_generation_completed', {
        songType: makeInstrumental ? 'instrumental' : 'song',
        model: makeInstrumental ? 'TemPolor i3.5' : 'TemPolor v3.5',
      })

      // Clear form and localStorage after successful submission
      setPrompt('')
      setLyrics('')
      setVoiceId('')
      setTitle('')
      setTags('')
      setValidationError(null)
      clearFormState()
    } catch (error) {
      console.error('Failed to generate:', error)

      // Track generation failure
      trackEvent('song_generation_failed', {
        songType: makeInstrumental ? 'instrumental' : 'song',
        model: makeInstrumental ? 'TemPolor i3.5' : 'TemPolor v3.5',
      })

      setValidationError(error instanceof Error ? error.message : 'Generation failed')
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <div className="terminal-border p-6 bg-black">
      <h2 className="text-3xl font-bold text-terminal-green mb-4 font-mono tracking-wider">
        [CREATE SONG]
      </h2>
      <p className="text-trail-tan font-mono text-sm mb-6">
        Generate AI songs with vocals using custom lyrics and music prompts
      </p>

      {validationError && (
        <div className="bg-red-900 border-2 border-red-500 rounded-lg p-4 mb-6">
          <p className="text-red-200 font-mono text-sm">{validationError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Music Theme/Style Prompt */}
        <div>
          <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
            ► MUSIC THEME / STYLE
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onBlur={handleBlur}
            placeholder="Describe the music style you want...
Example: 'upbeat pop with electronic synths'
Example: 'melancholic acoustic guitar ballad'
Example: 'energetic rock with electric guitars'"
            className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none resize-none retro-glow transition-all"
            rows={5}
            required
          />
        </div>

        {/* Lyrics Input */}
        <div>
          <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
            ► SONG LYRICS
          </label>
          <textarea
            value={lyrics}
            onChange={(e) => setLyrics(e.target.value)}
            onBlur={handleBlur}
            placeholder="Enter your song lyrics here...
[Verse 1]
Walking down the street...

[Chorus]
And I feel alive...

[Verse 2]
The sun is shining bright..."
            className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none resize-none retro-glow transition-all"
            rows={10}
            required={!makeInstrumental}
            disabled={makeInstrumental}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            data-1p-ignore
            data-lpignore="true"
          />
          <p className="text-trail-tan text-xs font-mono mt-2">
            💡 TIP: Use [Verse 1], [Chorus], [Bridge] tags for structure
          </p>
        </div>

        {/* Title (Optional) */}
        <div>
          <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
            ► TITLE <span className="text-trail-tan font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleBlur}
            placeholder="Enter song title..."
            className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
          />
        </div>

        {/* Tags (Optional) */}
        <div>
          <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
            ► TAGS <span className="text-trail-tan font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            onBlur={handleBlur}
            placeholder="e.g., rock, upbeat, energetic (comma-separated)"
            className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
          />
          <p className="text-trail-tan text-xs font-mono mt-2">
            💡 Comma-separated keywords for genre, mood, instruments, etc.
          </p>
        </div>

        {/* Voice ID (Optional) */}
        <div>
          <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
            ► VOICE ID <span className="text-trail-tan font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={voiceId}
            onChange={(e) => setVoiceId(e.target.value)}
            onBlur={handleBlur}
            placeholder="Enter voice ID for specific vocal style..."
            className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
            disabled={makeInstrumental}
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
          />
        </div>

        {/* Make Instrumental Toggle */}
        <div className="bg-black border-2 border-trail-tan p-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={makeInstrumental}
              onChange={(e) => {
                setMakeInstrumental(e.target.checked)
                handleBlur()
              }}
              className="w-5 h-5 bg-black border-2 border-sky-blue checked:bg-terminal-green checked:border-terminal-green focus:outline-none focus:ring-2 focus:ring-terminal-green cursor-pointer"
            />
            <span className="ml-3 text-sky-blue font-mono font-bold text-sm tracking-wider">
              MAKE INSTRUMENTAL (NO VOCALS)
            </span>
          </label>
          <p className="text-trail-tan text-xs font-mono mt-2 ml-8">
            Check this to generate instrumental music without vocals. Lyrics and Voice ID will be ignored.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Clear All Button */}
            <button
              type="button"
              onClick={handleClearAll}
              disabled={isGenerating}
              className="px-6 py-4 bg-black text-trail-tan font-bold font-mono text-base border-4 border-trail-tan hover:bg-trail-tan hover:text-black disabled:bg-gray-900 disabled:border-gray-700 disabled:text-gray-600 transition-all tracking-wider"
            >
              ✕ CLEAR ALL
            </button>

            {/* CREATE SONG Button */}
            <button
              type="submit"
              disabled={isGenerating || !prompt.trim() || (!makeInstrumental && !lyrics.trim())}
              className="px-8 py-4 bg-terminal-green text-white font-bold font-mono text-lg border-4 border-terminal-green hover:bg-black hover:text-terminal-green disabled:bg-gray-700 disabled:border-gray-700 disabled:text-gray-500 transition-all retro-glow tracking-wider"
            >
              {isGenerating ? '⏳ GENERATING...' : makeInstrumental ? '► CREATE INSTRUMENTAL ◄' : '► CREATE SONG ◄'}
            </button>
          </div>

          {/* Clear Local Storage Button */}
          <button
            type="button"
            onClick={handleClearLocalStorage}
            disabled={isGenerating}
            className="w-full px-6 py-3 bg-black text-sky-blue font-bold font-mono text-sm border-2 border-sky-blue hover:bg-sky-blue hover:text-black disabled:bg-gray-900 disabled:border-gray-700 disabled:text-gray-600 transition-all tracking-wider"
          >
            🗑️ CLEAR LOCAL STORAGE
          </button>
        </div>
      </form>

      {/* Info Box */}
      <div className="mt-6 bg-black border-2 border-trail-tan p-4">
        <p className="text-trail-tan font-mono text-xs mb-2">
          <span className="text-sky-blue">►</span> What you&apos;ll get:
        </p>
        <ul className="space-y-1 text-trail-tan font-mono text-xs">
          <li><span className="text-terminal-green">•</span> AI-generated song with vocals</li>
          <li><span className="text-terminal-green">•</span> Your custom lyrics sung by AI</li>
          <li><span className="text-terminal-green">•</span> Music matching your theme/style</li>
          <li><span className="text-terminal-green">•</span> Ready for vocabulary extraction</li>
        </ul>
      </div>
    </div>
  )
}

// SongCard component moved to @/components/SongCard.tsx
